import { useMemo } from 'react'

/**
 * Returns merge eligibility state from the preview API metadata.
 * Returns whether the merge is blocked (hard validation errors that prevent the merge entirely) or errors (e.g. unmapped jobs with affiliations that the user must resolve before proceeding).
 * Returns job-level aggregate affiliation data.
 *
 * @param {Object|undefined} meta - The `meta` object from the merge preview API response
 */
function useParseMergePreviewMeta(meta) {
  const preview = meta?.preview

  const previewJobsMap = useMemo(
    () =>
      new Map(preview?.sourceJobs?.concat(preview?.targetJobs)?.map(job => [job.entityId, job])),
    [preview],
  )

  const isBlocked = useMemo(
    () =>
      preview?.sourcePerson?.status === 'blocked' || preview?.targetPerson?.status === 'blocked',
    [preview],
  )

  const previewErrors = useMemo(() => {
    const sourceErrors =
      preview?.sourcePerson?.errors?.map(err => ({
        ...err,
        category: 'source',
        profile: 'Platform Profile',
      })) || []
    const targetErrors =
      preview?.targetPerson?.errors?.map(err => ({
        ...err,
        category: 'target',
        profile: 'FactSet Profile',
      })) || []

    return [...sourceErrors, ...targetErrors]
  }, [preview])

  const blockedSourceJobIds = useMemo(
    () => preview?.sourceJobs?.filter(j => j.status === 'blocked').map(j => j.entityId) || [],
    [preview],
  )

  return {
    preview,
    previewJobsMap,
    isBlocked,
    previewErrors,
    blockedSourceJobIds,
  }
}

export default useParseMergePreviewMeta
